import { Injectable, NgZone, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { SUPPORTED_EXTENSIONS } from '../utils/document-formats';

export interface PresignedPost {
  url: string;
  fields: Record<string, string>;
}

export type UploadEvent =
  | { type: 'progress'; loaded: number; total: number; percent: number }
  | { type: 'complete' };

/**
 * Uploads a file straight to S3 using a pre-signed POST.
 *
 * Built on a raw `XMLHttpRequest` rather than `HttpClient`: the request must reach S3 as
 * a plain multipart form with no `Authorization` header, which S3 rejects alongside the
 * policy's own credentials and which would add a forbidden header to the CORS preflight.
 */
@Injectable({ providedIn: 'root' })
export class S3UploadService {
  private readonly zone = inject(NgZone);

  upload(post: PresignedPost, file: File): Observable<UploadEvent> {
    return new Observable<UploadEvent>(observer => {
      const form = new FormData();
      // The signed fields must be echoed back verbatim, and S3 ignores every part after
      // the file, so the file is appended last.
      Object.entries(post.fields).forEach(([key, value]) => form.append(key, value));
      form.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', post.url, true);

      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (!event.lengthComputable) {
          return;
        }
        this.zone.run(() => observer.next({
          type: 'progress',
          loaded: event.loaded,
          total: event.total,
          percent: Math.round((event.loaded / event.total) * 100),
        }));
      };

      xhr.onload = () => this.zone.run(() => {
        if (xhr.status === 200 || xhr.status === 201 || xhr.status === 204) {
          observer.next({ type: 'complete' });
          observer.complete();
        } else {
          observer.error(new Error(this.describeS3Error(xhr.status, xhr.responseText)));
        }
      });

      xhr.onerror = () => this.zone.run(() =>
        observer.error(new Error('Network error during upload. Please try again.')));

      // Content-Type is deliberately unset so the browser generates the multipart boundary.
      xhr.send(form);

      return () => xhr.abort();
    });
  }

  /** S3 reports failures as an XML body, so the error code is the only usable signal. */
  private describeS3Error(status: number, body: string): string {
    const code = /<Code>([^<]+)<\/Code>/.exec(body)?.[1] ?? '';
    switch (code) {
      case 'EntityTooLarge': return 'This file is too large.';
      case 'EntityTooSmall': return 'This file appears to be empty.';
      case 'ExpiredToken': return 'Your upload link expired. Please try again.';
      case 'AccessDenied': return `Upload was rejected. Supported formats: ${SUPPORTED_EXTENSIONS.join(', ')}.`;
      case 'SignatureDoesNotMatch': return 'Upload could not be verified. Please try again.';
      default: return `Upload failed (${status}).`;
    }
  }
}
